import Container from "@client/content/Containers/container";
import Flag from "@client/content/controls/Flag/component";
import { getContainerProps, getEntityRef, getRootContainerProps } from "@client/content/lib/helper";
import { ModuleButton } from "@client/content/modules/Button";
import FormStyleProvider from "@client/content/Providers/FormStyle/container";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import Button from "@client/controls/Button/component";
import Hint from "@client/controls/Hint/component";
import { type OptionItem } from "@client/types";
import appInfo from "@common/appInfo";
import { validatePhone } from "@common/components/Form/Field/lib";
import { DynamicSystemLanguageSelect } from "@common/components/Inputs/Select/SystemLanguage";
import { UserProfilePictureWithUpload } from "@common/components/ProfilePicture/component";
import {
  FormButtons,
  FormControlled,
  type FormControlledRenderParams,
  FormField,
  FormGroup
} from "@common/controls/Form/component";
import { DynamicSelect } from "@common/controls/Inputs/Select/component";
import { EmailInput } from "@common/controls/Inputs/Text";
import AddressInput from "@common/controls/Inputs/Text/Address";
import { useI18n } from "@common/hooks/i18n";
import getOptions from "@common/lib/options";
import toasts from "@common/lib/toast";
import { userEmailValidator } from "@common/lib/validate";
import * as React from "react";
import api from "./api";
import ResetPasswordModal from "./ResetPasswordModal/container";
import { type MemberProfile } from "./types";

import "./style.less";

export type MapProps = RootContainerProps & {
  apiInfo: ModuleApiInfo;
  hasProfile: boolean;
  profile: MemberProfile.Profile;
};

export default (props: MapProps) => {
  const i18n = useI18n();

  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const submit = React.useCallback(
    async (data: MemberProfile.Profile): Promise<void> => {
      const res = await api.submit(props.apiInfo, { profile: data });
      if (res.error) throw new Error(res.error);

      toasts.saved();
    },
    [props.apiInfo]
  );

  const getLanguageCodeLabel = React.useCallback(
    (item: OptionItem<string>) => (
      <div className="fr fg sm">
        <Flag cultureCode={item.value} size={30} />
        <span>{item.label}</span>
        <Hint hint={item.value} />
      </div>
    ),
    []
  );

  return (
    <Container className="wsc_member_profile_content" {...getContainerProps(props)}>
      <FormStyleProvider {...getEntityRef(props)}>
        <FormControlled defaultValues={props.profile} submit={submit}>
          {({ onChange, isSubmitting, submit, values }: FormControlledRenderParams<MemberProfile.Profile>) => (
            <>
              <UserProfilePictureWithUpload
                nameTag="h3"
                noImage="initials"
                onChange={(x) => {
                  onChange("profilePictureMediaId", x?.mediaId);
                  onChange("profilePictureMediaSourceId", x?.sourceId);
                  onChange("profilePictureMediaSourceKey", x?.mediaSourceKey);
                }}
                size={64}
                uploadUrl="/api/site/v1/media/upload"
                user={values}
              />

              <FormGroup>
                <FormField
                  Component={AddressInput}
                  isDisabled={!props.hasProfile}
                  isRequired
                  label={i18n.email}
                  name="email"
                  validator={(i18n, v) => userEmailValidator(i18n, false, v, values.userId)}
                  value={values.email}
                />
                <FormField
                  Component={AddressInput}
                  isDisabled={!props.hasProfile}
                  label={i18n.phone}
                  name="telephone"
                  validator={validatePhone}
                  value={values.telephone}
                />
              </FormGroup>

              <FormGroup>
                <FormField
                  Component={AddressInput}
                  isDisabled={!props.hasProfile}
                  isRequired
                  label={i18n.firstName}
                  name="firstName"
                  value={values.firstName}
                />
                <FormField
                  Component={AddressInput}
                  isDisabled={!props.hasProfile}
                  isRequired
                  label={i18n.lastName}
                  name="lastName"
                  value={values.lastName}
                />
              </FormGroup>

              <FormGroup>
                <FormField
                  Component={DynamicSelect}
                  componentProps={{
                    fetchOptions: options.countries
                  }}
                  isDisabled={!props.hasProfile}
                  label={i18n.country}
                  name="countryCode"
                  value={values.countryCode}
                />
                <FormField
                  Component={DynamicSelect}
                  componentProps={{
                    dependent: values.countryCode,
                    fetchOptions: () => options.regions(values.countryCode)
                  }}
                  isDisabled={!props.hasProfile}
                  label={i18n.region}
                  name="subdivisionCode"
                  value={values.subdivisionCode}
                />
              </FormGroup>

              <FormGroup>
                <FormField
                  Component={AddressInput}
                  isDisabled={!props.hasProfile}
                  label={i18n.city}
                  name="city"
                  value={values.city}
                />
                <FormField
                  Component={AddressInput}
                  isDisabled={!props.hasProfile}
                  label={i18n.postalCode}
                  name="postalCode"
                  value={values.postalCode}
                />
              </FormGroup>

              <FormGroup>
                <FormField
                  Component={AddressInput}
                  isDisabled={!props.hasProfile}
                  label={i18n.street}
                  name="street"
                  value={values.street}
                />
                <FormField
                  Component={AddressInput}
                  isDisabled={!props.hasProfile}
                  label={i18n.unitAptSuite}
                  name="unit"
                  value={values.unit}
                />
              </FormGroup>

              <FormGroup>
                <FormField
                  Component={DynamicSelect}
                  componentProps={{
                    fetchOptions: options.timezones
                  }}
                  isDisabled={!props.hasProfile}
                  label={i18n.timeZone}
                  name="timeZoneKey"
                  value={values.timeZoneKey}
                />
                <FormField
                  Component={DynamicSystemLanguageSelect}
                  componentProps={{
                    fetchOptions: () => options.cultures(true)
                  }}
                  isRequired
                  label={i18n.language}
                  name="cultureCode"
                  value={values.cultureCode}
                />
              </FormGroup>

              <FormGroup>
                <FormField
                  colSpan={6}
                  Component={DynamicSelect}
                  componentProps={{
                    fetchOptions: options.getGetOptions("system", "currencies", {
                      isCommission: true
                    })
                  }}
                  isDisabled={!props.hasProfile}
                  label={i18n.commissionCurrency}
                  name="currencyCode"
                  value={values.currencyCode}
                />
              </FormGroup>

              {!appInfo.installedApps.includes("Wallets") && appInfo.user?.affiliateDate && (
                <FormField
                  Component={EmailInput}
                  isDisabled={!props.hasProfile}
                  label={i18n.email}
                  name="payPalEmail"
                  value={values.payPalEmail}
                />
              )}

              <FormButtons align="left">
                <ModuleButton
                  {...getRootContainerProps(props)}
                  isDisabled={!props.hasProfile || isSubmitting}
                  onClick={submit}
                />

                <ResetPasswordModal {...getRootContainerProps(props)}>
                  {({ show }) => (
                    <Button className="btn-link" isDisabled={!props.hasProfile} onClick={show}>
                      {i18n.resetPassword}
                    </Button>
                  )}
                </ResetPasswordModal>
              </FormButtons>
            </>
          )}
        </FormControlled>
      </FormStyleProvider>
    </Container>
  );
};
